import { useState, useEffect } from "react";
import { 
  Brain, RefreshCw, Sparkles, Users, CheckCircle, AlertCircle, 
  TrendingUp, BookOpen, Loader2, Play, ChevronDown, ChevronUp,
  FileText, Clock, Target, Zap
} from "lucide-react";
import getApiUrl from '../utils/getApiUrl';

const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


export default function SaniaLearningSection() {
  const [activeTab, setActiveTab] = useState("rachel");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Rachel stats
  const [rachelStats, setRachelStats] = useState(null);
  const [rachelLearning, setRachelLearning] = useState(false);
  const [rachelHistory, setRachelHistory] = useState([]);
  
  // SAN-IA stats (email)
  const [saniaData, setSaniaData] = useState(null);
  const [newPattern, setNewPattern] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadAllData();
    // eslint-disable-next-line
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([
        loadRachelStats(),
        loadSaniaData()
      ]);
    } catch (e) {
      console.error("Error loading data:", e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadRachelStats = async () => {
    try {
      const [statsRes, historyRes] = await Promise.all([
        authFetch(`${API}/rachel/knowledge-stats`),
        authFetch(`${API}/rachel/learning-history?limit=20`)
      ]);
      
      if (statsRes.ok) {
        const data = await statsRes.json();
        setRachelStats(data);
      }
      
      if (historyRes.ok) {
        const data = await historyRes.json();
        setRachelHistory(data.history || []);
      }
    } catch (e) {
      console.error("Erreur chargement Rachel:", e);
    }
  };

  const loadSaniaData = async () => {
    try {
      const r = await authFetch(`${API}/sania/learning/stats`);
      if (r.ok) setSaniaData(await r.json());
    } catch (e) { 
      console.error("Erreur chargement SAN-IA:", e); 
    }
  };

  const launchRachelLearning = async () => {
    setRachelLearning(true);
    try {
      const r = await authFetch(`${API}/rachel/auto-learn`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ use_ai: true })
      });
      
      if (r.ok) {
        const result = await r.json();
        alert(`✅ Rachel a appris ${result.newly_learned} nouveaux fournisseurs (${result.learned_with_ai} par IA)`);
        loadRachelStats();
      } else {
        alert("❌ Erreur lors de l'apprentissage");
      }
    } catch (e) {
      console.error("Erreur apprentissage Rachel:", e);
      alert("❌ Erreur lors de l'apprentissage");
    }
    setRachelLearning(false);
  };

  const addRejectRule = async (pattern) => {
    if (!pattern) return;
    setSaving(true);
    const fd = new FormData();
    fd.append("rule_type", "domain");
    fd.append("pattern", pattern);
    fd.append("action", "reject");
    try {
      await authFetch(`${API}/sania/learning/rules`, { method: "POST", body: fd });
      alert("Règle ajoutée !");
      setNewPattern("");
      loadSaniaData();
    } catch (e) { 
      console.error(e); 
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 animate-spin text-purple-500 mb-4" />
        <p className="text-slate-400">Chargement des données d'apprentissage...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-red-400">
        <AlertCircle className="w-8 h-8 mb-4" />
        <p>Erreur: {error}</p>
        <button onClick={loadAllData} className="mt-4 px-4 py-2 bg-slate-800 rounded-lg hover:bg-slate-700">
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-br from-purple-500/10 to-violet-500/10 border border-purple-500/30 rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-violet-600 rounded-xl flex items-center justify-center">
              <Brain className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Apprentissage SAN-IA</h2>
              <p className="text-slate-400">Historique d'apprentissage de tous les agents IA</p>
            </div>
          </div>
          <button 
            onClick={loadAllData} 
            className="p-2 bg-slate-800 hover:bg-slate-700 rounded-lg"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-700 pb-2">
        <button
          onClick={() => setActiveTab("rachel")}
          className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
            activeTab === "rachel" 
              ? 'bg-emerald-500/20 text-emerald-400 border-b-2 border-emerald-500' 
              : 'text-slate-400 hover:text-white'
          }`}
        >
          🧠 Rachel (Compta)
        </button>
        <button
          onClick={() => setActiveTab("sania")}
          className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
            activeTab === "sania" 
              ? 'bg-purple-500/20 text-purple-400 border-b-2 border-purple-500' 
              : 'text-slate-400 hover:text-white'
          }`}
        >
          📧 SAN-IA (Emails)
        </button>
        <button
          onClick={() => setActiveTab("all")}
          className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
            activeTab === "all" 
              ? 'bg-amber-500/20 text-amber-400 border-b-2 border-amber-500' 
              : 'text-slate-400 hover:text-white'
          }`}
        >
          📜 Tout l'historique
        </button>
      </div>

      {/* Rachel Tab */}
      {activeTab === "rachel" && (
        <RachelLearningTab 
          stats={rachelStats}
          history={rachelHistory}
          loading={rachelLearning}
          onLaunchLearning={launchRachelLearning}
          onRefresh={loadRachelStats}
        />
      )}

      {/* SAN-IA Tab */}
      {activeTab === "sania" && (
        <SaniaLearningTab 
          data={saniaData}
          newPattern={newPattern}
          setNewPattern={setNewPattern}
          saving={saving}
          onAddRule={addRejectRule}
          onRefresh={loadSaniaData}
        />
      )}

      {/* All History Tab */}
      {activeTab === "all" && (
        <AllHistoryTab history={rachelHistory} saniaData={saniaData} />
      )}
    </div>
  );
}

// Composant Rachel
function RachelLearningTab({ stats, history, loading, onLaunchLearning, onRefresh }) {
  const [showDetails, setShowDetails] = useState(false);
  const knowledge = stats?.knowledge || {};
  const lastLearning = stats?.last_learning;
  const topUnknown = stats?.top_unknown || [];

  return (
    <div className="space-y-6">
      {/* Stats principales */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 text-center">
          <BookOpen className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
          <p className="text-3xl font-bold text-emerald-400">{knowledge.total_suppliers_known || 0}</p>
          <p className="text-xs text-slate-400">Fournisseurs connus</p>
        </div>
        <div className="bg-violet-500/10 border border-violet-500/30 rounded-xl p-4 text-center">
          <Users className="w-6 h-6 text-violet-400 mx-auto mb-2" />
          <p className="text-3xl font-bold text-violet-400">{knowledge.total_customers_known || 0}</p>
          <p className="text-xs text-slate-400">Clients connus</p>
        </div>
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-center">
          <AlertCircle className="w-6 h-6 text-amber-400 mx-auto mb-2" />
          <p className="text-3xl font-bold text-amber-400">{knowledge.total_unknown || 0}</p>
          <p className="text-xs text-slate-400">Inconnus</p>
        </div>
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 text-center">
          <TrendingUp className="w-6 h-6 text-blue-400 mx-auto mb-2" />
          <p className="text-3xl font-bold text-blue-400">{knowledge.coverage_percent || 0}%</p>
          <p className="text-xs text-slate-400">Couverture</p>
        </div>
      </div>

      {/* Bouton lancement apprentissage */}
      <div className="bg-gradient-to-r from-emerald-500/10 to-violet-500/10 border border-emerald-500/30 rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-emerald-400" />
              Auto-Apprentissage Rachel
            </h3>
            <p className="text-sm text-slate-400 mt-1">
              Rachel analyse tous les bénéficiaires et apprend à les classifier automatiquement
            </p>
            {lastLearning && (
              <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Dernier apprentissage: {new Date(lastLearning.timestamp).toLocaleString('fr-FR')}
                {lastLearning.stats && (
                  <span className="text-emerald-400 ml-2">
                    (+{lastLearning.stats.newly_learned} nouveaux)
                  </span>
                )}
              </p>
            )}
          </div>
          <button
            onClick={onLaunchLearning}
            disabled={loading}
            className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 rounded-xl flex items-center gap-2 font-medium"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Apprentissage en cours...
              </>
            ) : (
              <>
                <Play className="w-5 h-5" />
                Lancer l'apprentissage
              </>
            )}
          </button>
        </div>
      </div>

      {/* Bénéficiaires inconnus */}
      {topUnknown.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl overflow-hidden">
          <button 
            onClick={() => setShowDetails(!showDetails)}
            className="w-full p-4 flex items-center justify-between hover:bg-amber-500/5"
          >
            <h3 className="font-semibold text-amber-400 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Top {topUnknown.length} Bénéficiaires Inconnus
            </h3>
            {showDetails ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          {showDetails && (
            <div className="border-t border-amber-500/30 divide-y divide-amber-500/20">
              {topUnknown.map((b, i) => (
                <div key={i} className="p-3 flex items-center justify-between">
                  <div>
                    <span className="font-medium">{b.supplier}</span>
                    {b.suggested_category && (
                      <span className="ml-2 px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded text-xs">
                        Suggestion: {b.suggested_category}
                      </span>
                    )}
                  </div>
                  <div className="text-right text-sm">
                    <span className="text-slate-400">{b.count} transactions</span>
                    <span className="text-slate-500 ml-2">
                      ({(b.total_amount || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}€)
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Historique apprentissage Rachel */}
      {history.length > 0 && (
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-slate-700">
            <h3 className="font-semibold text-emerald-400 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Historique d'apprentissage Rachel
            </h3>
          </div>
          <div className="max-h-80 overflow-y-auto divide-y divide-slate-700/50">
            {history.filter(h => h.agent_id === 'rachel').map((h, i) => (
              <LearningHistoryItem key={i} item={h} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Composant SAN-IA
function SaniaLearningTab({ data, newPattern, setNewPattern, saving, onAddRule, onRefresh }) {
  const stats = data?.stats || {};
  const patterns = data?.rejection_patterns || [];
  const rules = data?.manual_rules || [];
  const decisions = data?.recent_decisions || [];

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-5 gap-4">
        <div className="bg-slate-800/50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-purple-400">{stats.total_processed || 0}</p>
          <p className="text-xs text-slate-400">Total traité</p>
        </div>
        <div className="bg-slate-800/50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-green-400">{stats.auto_linked || 0}</p>
          <p className="text-xs text-slate-400">Auto-liés</p>
        </div>
        <div className="bg-slate-800/50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-amber-400">{stats.manual_linked || 0}</p>
          <p className="text-xs text-slate-400">Manuels</p>
        </div>
        <div className="bg-slate-800/50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-red-400">{stats.rejected || 0}</p>
          <p className="text-xs text-slate-400">Rejetés</p>
        </div>
        <div className="bg-slate-800/50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-cyan-400">{stats.accuracy_rate || 0}%</p>
          <p className="text-xs text-slate-400">Précision</p>
        </div>
      </div>

      {/* Suggestions rejets */}
      {patterns.length > 0 && (
        <div className="bg-amber-900/20 border border-amber-500/30 rounded-xl p-4">
          <h3 className="font-semibold text-amber-400 mb-4">💡 Domaines souvent rejetés</h3>
          <div className="space-y-2">
            {patterns.map((p, i) => (
              <div key={i} className="flex items-center justify-between bg-slate-800/50 rounded-lg p-3">
                <span className="text-red-400 font-mono">@{p.domain}</span>
                <span className="text-xs text-slate-400">{p.count} rejets</span>
                <button 
                  onClick={() => onAddRule(p.domain)}
                  className="px-3 py-1 bg-amber-600 hover:bg-amber-700 rounded text-sm"
                >
                  Rejeter auto
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Ajouter règle */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
        <h3 className="font-semibold text-green-400 mb-4">➕ Ajouter une règle de rejet</h3>
        <div className="flex gap-4">
          <input
            type="text"
            value={newPattern}
            onChange={(e) => setNewPattern(e.target.value)}
            placeholder="Domaine à rejeter (ex: spam.com)"
            className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-4 py-2"
          />
          <button
            onClick={() => onAddRule(newPattern)}
            disabled={saving || !newPattern}
            className="px-6 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 rounded-lg font-medium"
          >
            {saving ? "..." : "Ajouter"}
          </button>
        </div>
      </div>

      {/* Règles actives */}
      {rules.length > 0 && (
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl">
          <div className="p-4 border-b border-slate-700">
            <h3 className="font-semibold text-blue-400">📋 Règles actives ({rules.length})</h3>
          </div>
          <div className="divide-y divide-slate-700/50">
            {rules.map((r, i) => (
              <div key={i} className="p-3 flex items-center gap-3">
                <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs">{r.action}</span>
                <span className="text-slate-400">{r.rule_type}:</span>
                <span className="font-mono text-cyan-400">{r.pattern}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Décisions récentes */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl">
        <div className="p-4 border-b border-slate-700">
          <h3 className="font-semibold text-purple-400">📜 Décisions récentes</h3>
        </div>
        <div className="max-h-64 overflow-y-auto divide-y divide-slate-700/50">
          {decisions.length === 0 ? (
            <p className="p-4 text-slate-500 text-center">Aucune décision</p>
          ) : decisions.map((d, i) => (
            <div key={i} className="p-3 flex items-center gap-3">
              <span className={d.type === "rejection" ? "text-red-400" : d.auto ? "text-purple-400" : "text-amber-400"}>
                {d.type === "rejection" ? "🚫" : d.auto ? "🤖" : "👤"}
              </span>
              <span className="flex-1 truncate">{d.subject || "(Sans sujet)"}</span>
              <span className="text-sm text-slate-400">
                {d.type === "rejection" ? d.reason : d.client_name}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Composant historique global
function AllHistoryTab({ history, saniaData }) {
  // Combiner les historiques
  const allHistory = [
    ...history.map(h => ({ ...h, agent: 'rachel' })),
    // Ajouter SAN-IA decisions comme historique
    ...(saniaData?.recent_decisions || []).map(d => ({
      agent_id: 'sania',
      action: d.type,
      timestamp: d.date,
      stats: { processed: 1 },
      details: [d]
    }))
  ].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
      <div className="p-4 border-b border-slate-700">
        <h3 className="font-semibold flex items-center gap-2">
          <FileText className="w-5 h-5 text-amber-400" />
          Historique complet de tous les agents
        </h3>
      </div>
      <div className="max-h-[600px] overflow-y-auto divide-y divide-slate-700/50">
        {allHistory.length === 0 ? (
          <p className="p-8 text-slate-500 text-center">Aucun historique d'apprentissage</p>
        ) : allHistory.map((h, i) => (
          <LearningHistoryItem key={i} item={h} showAgent />
        ))}
      </div>
    </div>
  );
}

// Item d'historique
function LearningHistoryItem({ item, showAgent = false }) {
  const [expanded, setExpanded] = useState(false);
  
  const getAgentColors = (agentId) => {
    if (agentId === 'rachel') return { bg: 'bg-emerald-500/20', text: 'text-emerald-400', badge: 'bg-emerald-500/20 text-emerald-400' };
    if (agentId === 'sania') return { bg: 'bg-purple-500/20', text: 'text-purple-400', badge: 'bg-purple-500/20 text-purple-400' };
    return { bg: 'bg-slate-500/20', text: 'text-slate-400', badge: 'bg-slate-500/20 text-slate-400' };
  };
  
  const colors = getAgentColors(item.agent_id);
  
  return (
    <div className="p-3 hover:bg-slate-800/30">
      <button 
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left flex items-start gap-3"
      >
        <div className={`w-8 h-8 ${colors.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
          {item.agent_id === 'rachel' ? (
            <Target className={`w-4 h-4 ${colors.text}`} />
          ) : (
            <Zap className={`w-4 h-4 ${colors.text}`} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {showAgent && (
              <span className={`px-2 py-0.5 ${colors.badge} rounded text-xs`}>
                {item.agent_id}
              </span>
            )}
            <span className="font-medium">
              {item.action === 'auto_learn_beneficiaries' ? 'Auto-apprentissage bénéficiaires' : item.action}
            </span>
          </div>
          <div className="flex items-center gap-4 mt-1 text-sm">
            <span className="text-slate-500">
              {new Date(item.timestamp).toLocaleString('fr-FR')}
            </span>
            {item.stats && (
              <>
                <span className="text-emerald-400">+{item.stats.newly_learned || 0} nouveaux</span>
                {item.stats.learned_with_ai > 0 && (
                  <span className="text-violet-400">({item.stats.learned_with_ai} IA)</span>
                )}
              </>
            )}
          </div>
        </div>
        {item.details_count > 0 && (
          <span className="text-slate-500 text-sm">
            {item.details_count} détails
            {expanded ? <ChevronUp className="w-4 h-4 inline ml-1" /> : <ChevronDown className="w-4 h-4 inline ml-1" />}
          </span>
        )}
      </button>
      
      {expanded && item.details && item.details.length > 0 && (
        <div className="mt-3 ml-11 bg-slate-900/50 rounded-lg p-3 space-y-1 text-sm">
          {item.details.slice(0, 10).map((d, i) => (
            <div key={i} className="flex items-center gap-2">
              <CheckCircle className="w-3 h-3 text-emerald-400" />
              <span className="text-slate-300">{d.supplier}</span>
              <span className="text-slate-500">→</span>
              <span className="text-amber-400">{d.category}</span>
              <span className="text-xs text-slate-500">({Math.round((d.confidence || 0) * 100)}%)</span>
            </div>
          ))}
          {item.details.length > 10 && (
            <p className="text-slate-500 text-xs mt-2">
              ... et {item.details.length - 10} autres
            </p>
          )}
        </div>
      )}
    </div>
  );
}
